import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Download, Upload, ShieldAlert, DatabaseBackup, PackageCheck,
  Loader2, FileJson, CheckCircle2, RotateCcw, History, Database, Files,
} from "lucide-react";

/**
 * Full System Migration — backup & restore the ENTIRE application state:
 *   • Every MongoDB collection (orders, customers, invoices, users, CMS, integrations, ...)
 *   • Every uploaded file under /uploads (CMS media, invoice PDFs, KYC docs, lab reports)
 *
 * Encrypted with user passphrase (PBKDF2 + Fernet). Server never persists the passphrase.
 * Use it to move between Preview ↔ Production or as a manual disaster-recovery backup.
 */
export default function EnvMigration({ embedded = false }) {
  const containerCls = embedded
    ? "space-y-6"
    : "mx-auto max-w-5xl space-y-6 p-4 sm:p-6";
  return (
    <div className={containerCls}>
      {!embedded && (
        <header className="space-y-2">
          <div className="flex items-center gap-3">
            <DatabaseBackup className="h-7 w-7 text-primary" />
            <h1 className="text-2xl font-bold sm:text-3xl" data-testid="migration-page-title">
              Full System Backup &amp; Migration
            </h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Encrypted backup of the entire application — every database collection (orders, customers,
            invoices, CMS, integrations, users) plus every uploaded file (CMS media, PDFs, KYC docs).
            Use it to migrate Preview ↔ Production or as a manual disaster-recovery snapshot.
          </p>
        </header>
      )}

      {embedded && (
        <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <DatabaseBackup className="h-4 w-4 text-primary" />
            Full System Backup &amp; Environment Migration
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            One-click encrypted backup of the entire application state — every database collection
            and every uploaded file. Use it to sync Preview ↔ Production or as a disaster-recovery snapshot.
          </p>
        </div>
      )}

      <Alert>
        <ShieldAlert className="h-4 w-4" />
        <AlertDescription className="text-xs">
          Choose a strong passphrase (min 8 chars) — you&apos;ll need the same passphrase to import
          the bundle. Store it somewhere safe. If lost, the bundle cannot be decrypted.
          The server never stores your passphrase.
        </AlertDescription>
      </Alert>

      <ExportPanel />
      <ImportPanel />
      <BackupsPanel />
    </div>
  );
}

/* ─────────────────────────── EXPORT ─────────────────────────── */
function ExportPanel() {
  const [passphrase, setPassphrase] = useState("");
  const [includeCollections, setIncludeCollections] = useState(true);
  const [includeUploads, setIncludeUploads] = useState(true);
  const [includeTransient, setIncludeTransient] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleExport = async () => {
    if (passphrase.length < 8) {
      toast.error("Passphrase must be at least 8 characters");
      return;
    }
    if (!includeCollections && !includeUploads) {
      toast.error("Select at least one thing to back up");
      return;
    }
    setBusy(true);
    const t = toast.loading("Building encrypted bundle — this may take a minute for large datasets…");
    try {
      const res = await api.post(
        "/cms/bundle/export",
        {
          passphrase,
          include_collections: includeCollections,
          include_uploads: includeUploads,
          include_transient: includeTransient,
        },
        { responseType: "blob", timeout: 300000 },  // 5 min timeout for big datasets
      );
      const blob = new Blob([res.data], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.href = url;
      a.download = `fuelexpress-full-backup-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Full backup downloaded — keep it and the passphrase safe", { id: t });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed", { id: t });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        <div className="flex items-center gap-2">
          <Download className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">Export full backup</h2>
        </div>
        <p className="text-xs text-muted-foreground">
          Downloads an encrypted JSON containing everything. Run this in the source environment.
        </p>

        <div className="grid gap-3 sm:grid-cols-3">
          <label className="flex items-start gap-2 rounded-md border p-3">
            <Checkbox
              checked={includeCollections}
              onCheckedChange={(v) => setIncludeCollections(!!v)}
              data-testid="export-include-collections"
              className="mt-0.5"
            />
            <div className="text-sm">
              <div className="flex items-center gap-1.5 font-medium">
                <Database className="h-3.5 w-3.5" /> Database
              </div>
              <div className="text-xs text-muted-foreground">
                All collections: orders, customers, invoices, CMS, integrations, users, etc.
              </div>
            </div>
          </label>
          <label className="flex items-start gap-2 rounded-md border p-3">
            <Checkbox
              checked={includeUploads}
              onCheckedChange={(v) => setIncludeUploads(!!v)}
              data-testid="export-include-uploads"
              className="mt-0.5"
            />
            <div className="text-sm">
              <div className="flex items-center gap-1.5 font-medium">
                <Files className="h-3.5 w-3.5" /> Uploaded files
              </div>
              <div className="text-xs text-muted-foreground">
                CMS media, invoice PDFs, KYC docs, lab reports, attendance photos
              </div>
            </div>
          </label>
          <label className="flex items-start gap-2 rounded-md border p-3">
            <Checkbox
              checked={includeTransient}
              onCheckedChange={(v) => setIncludeTransient(!!v)}
              data-testid="export-include-transient"
              className="mt-0.5"
            />
            <div className="text-sm">
              <div className="font-medium">Transient logs</div>
              <div className="text-xs text-muted-foreground">
                OTPs, SMS/email logs, notifications, GPS pings. Off by default.
              </div>
            </div>
          </label>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="export-passphrase" className="text-xs">Encryption passphrase (min 8 chars)</Label>
          <Input
            id="export-passphrase"
            type="password"
            placeholder="Choose a strong passphrase"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            data-testid="export-passphrase-input"
          />
        </div>

        <Button
          onClick={handleExport}
          disabled={busy}
          className="w-full sm:w-auto"
          data-testid="export-download-btn"
        >
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
          Download encrypted backup
        </Button>
      </CardContent>
    </Card>
  );
}

/* ─────────────────────────── IMPORT ─────────────────────────── */
function ImportPanel() {
  const [file, setFile] = useState(null);
  const [passphrase, setPassphrase] = useState("");
  const [preview, setPreview] = useState(null);
  const [applyCollections, setApplyCollections] = useState(true);
  const [applyUploads, setApplyUploads] = useState(true);
  const [mode, setMode] = useState("replace");
  const [overwriteFiles, setOverwriteFiles] = useState(true);
  const [busy, setBusy] = useState(false);
  const [imported, setImported] = useState(null);

  const handlePreview = async () => {
    if (!file) return toast.error("Choose a bundle file first");
    if (passphrase.length < 8) return toast.error("Enter the passphrase used at export");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("passphrase", passphrase);
      const res = await api.post("/cms/bundle/preview", fd, { timeout: 120000 });
      setPreview(res.data);
      toast.success("Bundle decrypted — review below, then apply");
    } catch (err) {
      setPreview(null);
      toast.error(err?.response?.data?.detail || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const handleApply = async () => {
    if (!file) return;
    if (!applyCollections && !applyUploads) {
      toast.error("Select at least one section to apply");
      return;
    }
    const warn = mode === "replace"
      ? "REPLACE MODE will WIPE current collections before restoring. This is destructive. Continue?"
      : "MERGE MODE will upsert documents by _id. Existing docs may be overwritten. Continue?";
    if (!window.confirm(warn)) return;
    setBusy(true);
    const t = toast.loading("Applying bundle — do not close this page…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("passphrase", passphrase);
      fd.append("apply_collections", String(applyCollections));
      fd.append("apply_uploads", String(applyUploads));
      fd.append("mode", mode);
      fd.append("overwrite_files", String(overwriteFiles));
      const res = await api.post("/cms/bundle/import", fd, { timeout: 600000 });
      setImported(res.data);
      toast.success("Bundle applied — reload the app to see changes", { id: t });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed", { id: t });
    } finally {
      setBusy(false);
    }
  };

  const c = preview?.contents || {};
  const isV2 = preview?.version === "2.0";

  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        <div className="flex items-center gap-2">
          <Upload className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">Restore from backup</h2>
        </div>
        <p className="text-xs text-muted-foreground">
          Upload a bundle exported from any FuelExpress environment. A safety snapshot of
          current CMS + integrations is taken before overwrite.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="import-file" className="text-xs">Bundle file (.json)</Label>
            <Input
              id="import-file"
              type="file"
              accept="application/json,.json"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setImported(null); }}
              data-testid="import-file-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="import-passphrase" className="text-xs">Passphrase used at export</Label>
            <Input
              id="import-passphrase"
              type="password"
              placeholder="Same passphrase used when exporting"
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
              data-testid="import-passphrase-input"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={handlePreview}
            disabled={busy || !file || !passphrase}
            data-testid="import-preview-btn"
          >
            {busy && !preview ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileJson className="mr-2 h-4 w-4" />}
            Decrypt &amp; preview
          </Button>
        </div>

        {preview && (
          <div className="space-y-3 rounded-md border bg-muted/30 p-4">
            <div className="flex items-center gap-2 text-sm font-medium">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              Bundle decrypted — version {preview.version}
            </div>
            <div className="text-xs text-muted-foreground">
              Exported at: <span className="font-mono">{preview.exported_at || "—"}</span>
            </div>

            <div className="flex flex-wrap gap-2">
              {isV2 && c.collections && (
                <Badge variant="secondary">
                  <Database className="mr-1 h-3 w-3" />
                  {Object.keys(c.collections).length} collections · {c.total_docs || 0} docs
                </Badge>
              )}
              {isV2 && c.uploads_count > 0 && (
                <Badge variant="secondary">
                  <Files className="mr-1 h-3 w-3" />
                  {c.uploads_count} files · {(c.uploads_bytes / 1024).toFixed(0)} KB
                </Badge>
              )}
              {!isV2 && (c.legacy_cms || c.legacy_integrations) && (
                <Badge variant="outline">
                  Legacy v1.0 bundle — {[c.legacy_cms && "CMS", c.legacy_integrations && "Integrations"].filter(Boolean).join(" + ")}
                </Badge>
              )}
            </div>

            {isV2 && c.collections && (
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground">
                  Collections in bundle ({Object.keys(c.collections).length})
                </summary>
                <div className="mt-2 grid grid-cols-2 gap-1 sm:grid-cols-3">
                  {Object.entries(c.collections).sort().map(([n, count]) => (
                    <div key={n} className="flex justify-between rounded bg-background px-2 py-1">
                      <code className="text-[11px]">{n}</code>
                      <span className="text-[11px] text-muted-foreground">{count}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}

            <div className="grid gap-3 border-t pt-3 sm:grid-cols-2">
              {(isV2 ? c.collections : c.legacy_cms || c.legacy_integrations) && (
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={applyCollections}
                    onCheckedChange={(v) => setApplyCollections(!!v)}
                    data-testid="import-apply-collections"
                  />
                  Apply database collections
                </label>
              )}
              {c.uploads_count > 0 && (
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={applyUploads}
                    onCheckedChange={(v) => setApplyUploads(!!v)}
                    data-testid="import-apply-uploads"
                  />
                  Restore uploaded files
                </label>
              )}
            </div>

            <div className="grid gap-3 border-t pt-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Collection write mode</Label>
                <Select value={mode} onValueChange={setMode}>
                  <SelectTrigger className="w-full" data-testid="import-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="replace">Replace (wipe &amp; restore — destructive)</SelectItem>
                    <SelectItem value="merge">Merge (upsert by _id — additive)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {c.uploads_count > 0 && (
                <label className="flex items-center gap-2 self-end text-sm">
                  <Checkbox
                    checked={overwriteFiles}
                    onCheckedChange={(v) => setOverwriteFiles(!!v)}
                    data-testid="import-overwrite-files"
                  />
                  Overwrite existing files
                </label>
              )}
            </div>

            <Button
              onClick={handleApply}
              disabled={busy}
              className="w-full sm:w-auto"
              variant={mode === "replace" ? "destructive" : "default"}
              data-testid="import-apply-btn"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PackageCheck className="mr-2 h-4 w-4" />}
              Apply to this environment
            </Button>
          </div>
        )}

        {imported && (
          <Alert className="border-green-500/50 bg-green-500/5">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-xs space-y-1">
              <div>Restored successfully. Backup snapshot ID: <code className="font-mono">{imported.backup_id}</code></div>
              {imported.applied?.collections && (
                <div>Collections: {Object.keys(imported.applied.collections).length} written</div>
              )}
              {imported.applied?.uploads && (
                <div>Files: {imported.applied.uploads.written} written, {imported.applied.uploads.skipped} skipped</div>
              )}
              <div className="pt-1">Reload the app (Ctrl+R) to see changes across all modules.</div>
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

/* ─────────────────────────── BACKUPS ─────────────────────────── */
function BackupsPanel() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/cms/bundle/backups");
      setBackups(res.data.backups || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load backups");
    } finally {
      setLoading(false);
    }
  };

  const restore = async (backup_id) => {
    if (!window.confirm(`Restore pre-import snapshot from ${backup_id}? This only restores CMS + integrations (not full DB).`)) {
      return;
    }
    setRestoring(backup_id);
    try {
      await api.post(`/cms/bundle/backups/${encodeURIComponent(backup_id)}/restore`);
      toast.success("Snapshot restored — reload the app to see changes");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Restore failed");
    } finally {
      setRestoring(null);
    }
  };

  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">Pre-import snapshots</h2>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="backups-refresh-btn">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <History className="mr-2 h-4 w-4" />}
            Load snapshots
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Every import auto-saves a pre-overwrite snapshot of CMS + integrations. Use these
          to roll back if a restore goes wrong. For full-DB rollback, keep your bundle files.
        </p>

        {backups.length === 0 && !loading && (
          <div className="rounded-md border border-dashed p-6 text-center text-xs text-muted-foreground">
            No snapshots yet. Click &quot;Load snapshots&quot; to fetch, or run an import first.
          </div>
        )}

        {backups.length > 0 && (
          <div className="space-y-2">
            {backups.map((b) => (
              <div
                key={b.backup_id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3 text-sm"
              >
                <div className="min-w-0 space-y-0.5">
                  <div className="truncate font-mono text-xs">{b.backup_id}</div>
                  <div className="text-xs text-muted-foreground">
                    Created by {b.created_by || "—"}
                    {b.source_meta?.source_env && ` · source: ${b.source_meta.source_env}`}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => restore(b.backup_id)}
                  disabled={restoring === b.backup_id}
                  data-testid={`backup-restore-${b.backup_id}`}
                >
                  {restoring === b.backup_id ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <RotateCcw className="mr-2 h-4 w-4" />
                  )}
                  Restore
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
